import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface BaselineComparisonChartProps {
  data: {
    baseYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    selectedYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    baseYearValue: number;
    selectedYearValue: number;
  } | undefined;
}

export function BaselineComparisonChart({ data }: BaselineComparisonChartProps) {
  if (!data) return null;

  const chartData = [
    {
      name: 'Scope 1',
      baseYear: data.baseYear.scope1,
      selectedYear: data.selectedYear.scope1,
      variance: data.selectedYear.scope1 - data.baseYear.scope1,
      variancePercent: data.baseYear.scope1 > 0 ? ((data.selectedYear.scope1 - data.baseYear.scope1) / data.baseYear.scope1) * 100 : 0,
    },
    {
      name: 'Scope 2',
      baseYear: data.baseYear.scope2,
      selectedYear: data.selectedYear.scope2,
      variance: data.selectedYear.scope2 - data.baseYear.scope2,
      variancePercent: data.baseYear.scope2 > 0 ? ((data.selectedYear.scope2 - data.baseYear.scope2) / data.baseYear.scope2) * 100 : 0,
    },
    {
      name: 'Scope 3',
      baseYear: data.baseYear.scope3,
      selectedYear: data.selectedYear.scope3,
      variance: data.selectedYear.scope3 - data.baseYear.scope3,
      variancePercent: data.baseYear.scope3 > 0 ? ((data.selectedYear.scope3 - data.baseYear.scope3) / data.baseYear.scope3) * 100 : 0,
    },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const baseYearData = payload.find((p: any) => p.dataKey === 'baseYear');
      const selectedYearData = payload.find((p: any) => p.dataKey === 'selectedYear');
      
      if (baseYearData && selectedYearData) {
        const variance = selectedYearData.value - baseYearData.value;
        const variancePercent = baseYearData.value > 0 ? (variance / baseYearData.value) * 100 : 0;
        
        return (
          <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium mb-2">{label} Emissions</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-600">Base Year ({data.baseYearValue}):</span>
                <span className="font-medium">{baseYearData.value.toFixed(2)} kg CO2e</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-600">Selected Year ({data.selectedYearValue}):</span>
                <span className="font-medium">{selectedYearData.value.toFixed(2)} kg CO2e</span>
              </div>
              <div className="flex justify-between pt-1 border-t">
                <span className="font-medium">Variance:</span>
                <span className={`font-medium ${variance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {variance >= 0 ? '+' : ''}{variance.toFixed(2)} kg CO2e ({variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%)
                </span>
              </div>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar 
            dataKey="baseYear" 
            fill="#3b82f6" 
            name={`Base Year (${data.baseYearValue})`}
            radius={[2, 2, 0, 0]} 
          />
          <Bar 
            dataKey="selectedYear" 
            fill="#10b981" 
            name={`Selected Year (${data.selectedYearValue})`}
            radius={[2, 2, 0, 0]} 
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}



